import { Grid, Paper, Stack, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { Bar } from "react-chartjs-2";
import React from "react";
import { useParams } from "react-router-dom";
import Table from "../../components/table";
import { GET_INSTAGRAM_ENGAGEMENT } from "../../services/instagram_analytics";
import {
  AnalyticsEmptyState,
  InstagramAnalyticsHeader,
  StatCard,
  useInstagramAccounts,
  useInstagramSync,
} from "./shared";

type EngagementRow = {
  ig_media_id: string;
  caption?: string;
  caption_short?: string;
  short_label?: string;
  media_type?: string;
  likes?: number;
  comments?: number;
  saves?: number;
  engagement_rate?: number; // decimal (ex: 0.043)
  engagement_rate_pct?: number; // pct (ex: 4.3)
  _engagement_rate_pct: number; // normalizado para %
};

function toNumber(value: any, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function resolveEngagementPct(item: any): number {
  if (item?.engagement_rate_pct !== undefined && item?.engagement_rate_pct !== null) {
    return toNumber(item.engagement_rate_pct, 0);
  }
  if (item?.engagement_rate !== undefined && item?.engagement_rate !== null) {
    return toNumber(item.engagement_rate, 0) * 100;
  }
  return 0;
}

export default function InstagramAnalyticsEngagementPage() {
  const { oauthId } = useParams();
  const { accounts, loadingAccounts } = useInstagramAccounts();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [rows, setRows] = React.useState<EngagementRow[]>([]);
  const [avgEngagementRatePct, setAvgEngagementRatePct] = React.useState<number | null>(null);

  const loadData = React.useCallback(async () => {
    if (!oauthId) return;

    try {
      setLoading(true);
      const response = await GET_INSTAGRAM_ENGAGEMENT(oauthId);

      const normalizedRows: EngagementRow[] = (response?.engagement_per_post || []).map((item: any) => ({
        ...item,
        _engagement_rate_pct: resolveEngagementPct(item),
      }));

      const avgPctRaw =
        response?.avg_engagement_rate_pct ??
        (response?.avg_engagement_rate !== undefined && response?.avg_engagement_rate !== null
          ? Number(response.avg_engagement_rate) * 100
          : null);

      setRows(normalizedRows);
      setAvgEngagementRatePct(avgPctRaw === null ? null : toNumber(avgPctRaw, 0));
    } catch (err: any) {
      showNotification({ message: err.message, color: "red" });
    } finally {
      setLoading(false);
    }
  }, [oauthId]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const { syncing, triggerSync } = useInstagramSync(oauthId, loadData);

  const isEmpty = !loading && rows.length === 0;

  const chartRows = React.useMemo(
    () => [...rows].sort((a, b) => b._engagement_rate_pct - a._engagement_rate_pct).slice(0, 15),
    [rows]
  );

  return (
    <Stack>
      <InstagramAnalyticsHeader
        oauthId={oauthId}
        title="Relatório de engajamento"
        subtitle="Top publicações por taxa de engajamento"
        active="engagement"
        accounts={accounts}
        loadingAccounts={loadingAccounts}
        syncing={syncing}
        onSync={triggerSync}
      />

      {isEmpty ? (
        <AnalyticsEmptyState oauthId={oauthId} syncing={syncing} onSync={triggerSync} />
      ) : (
        <>
          <Grid>
            <Grid.Col md={4}>
              <StatCard
                label="Taxa média de engajamento"
                value={avgEngagementRatePct === null ? "-" : `${avgEngagementRatePct.toFixed(2)}%`}
                color="violet"
              />
            </Grid.Col>
            <Grid.Col md={8}>
              <Paper withBorder p="md" h="100%">
                <Text fw={600}>Fórmula</Text>
                <Text size="sm" c="dimmed">
                  (curtidas + comentários + salvamentos) / seguidores × 100
                </Text>
              </Paper>
            </Grid.Col>
          </Grid>

          <Paper withBorder p="md">
            <Text fw={600} mb={6}>
              Top 15 publicações por taxa de engajamento (%)
            </Text>
            <Text size="sm" c="dimmed" mb="md">
              Quanto maior a barra, maior o engajamento relativo da publicação.
            </Text>

            <div style={{ height: Math.max(360, chartRows.length * 34) }}>
              <Bar
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: "y" as const,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        title: (items: any) => {
                          const idx = items?.[0]?.dataIndex ?? 0;
                          const row = chartRows[idx];
                          return row?.caption_short || row?.caption || "Publicação";
                        },
                        label: (ctx: any) => {
                          const idx = ctx?.dataIndex ?? 0;
                          const row = chartRows[idx];
                          const pct = toNumber(row?._engagement_rate_pct, 0).toFixed(2);
                          const likes = toNumber(row?.likes, 0);
                          const comments = toNumber(row?.comments, 0);
                          const saves = toNumber(row?.saves, 0);
                          const mediaType = row?.media_type || "POST";
                          const data = row?.short_label || "-";
                          return ` ${pct}% • ${mediaType} • ${data} • ${likes} curtidas • ${comments} comentários • ${saves} salvamentos`;
                        },
                      },
                    },
                  },
                  scales: {
                    x: {
                      title: { display: true, text: "Taxa de engajamento (%)" },
                      ticks: {
                        callback: (value: any) => `${value}%`,
                      },
                    },
                    y: {
                      ticks: {
                        autoSkip: false,
                      },
                    },
                  },
                }}
                data={{
                  labels: chartRows.map((item, index) => item.short_label || `Post ${index + 1}`),
                  datasets: [
                    {
                      label: "Taxa de engajamento (%)",
                      data: chartRows.map((item) => toNumber(item._engagement_rate_pct, 0)),
                      backgroundColor: "#7c3aed88",
                      borderColor: "#7c3aed",
                      borderWidth: 1,
                    },
                  ],
                }}
              />
            </div>
          </Paper>

          <Table
            data={rows}
            loading={loading}
            columns={[
              {
                key: "caption",
                title: "Publicação",
                render: (item) => <Text lineClamp={1}>{item.caption_short || item.caption || "-"}</Text>,
              },
              { key: "short_label", title: "Data" },
              { key: "media_type", title: "Tipo" },
              { key: "likes", title: "Curtidas" },
              { key: "comments", title: "Comentários" },
              { key: "saves", title: "Salvamentos" },
              {
                key: "_engagement_rate_pct",
                title: "Taxa de engajamento",
                render: (item) => `${toNumber(item._engagement_rate_pct, 0).toFixed(2)}%`,
              },
            ]}
          />
        </>
      )}
    </Stack>
  );
}